/**
 * Maps Add Customer UI form state to Java onboarding + Python commercial profile APIs.
 * UI keeps human-readable labels; wire values follow ui-integration-guide.md /
 * ui-api-change-payloads.md.
 */

const parseNumber = (value) => {
  const cleaned = String(value || "").replace(/[$,%/]/g, "").trim();
  if (!cleaned) return null;
  const amount = Number(cleaned);
  return Number.isFinite(amount) ? amount : NaN;
};

const toMoney = (value) => {
  const amount = parseNumber(value);
  if (amount == null || Number.isNaN(amount)) return undefined;
  return amount.toFixed(2);
};

const toPercent = (value) => {
  const amount = parseNumber(value);
  if (amount == null || Number.isNaN(amount)) return undefined;
  return amount.toFixed(2);
};

const paymentTermsDays = (label) => {
  const match = String(label || "").match(/^Net\s+(\d+)$/i);
  return match ? Number(match[1]) : undefined;
};

const PRICING_MODEL = {
  "Standard List Minus Margin": "STANDARD_LIST_MINUS_MARGIN",
  "Cost-Plus Markup": "COST_PLUS_MARKUP",
  "Custom Rate Card": "CUSTOM_RATE_CARD",
};

/** UI chip labels → API exclusion codes (docs use spaced Marketplace example). */
const SERVICE_EXCLUSION = {
  "Exclude AWS Marketplace": "AWS Marketplace",
  "Exclude Support Fees": "AWS Support",
  "Exclude Taxes": "Taxes",
};

const setIf = (body, key, value) => {
  if (value === undefined || value === null || value === "") return;
  body[key] = value;
};

export const distinctAccountIds = (values) => {
  const next = [
    ...new Set(
      (values || [])
        .map((id) => String(id || "").trim())
        .filter((id) => /^\d{12}$/.test(id))
    ),
  ];
  return next;
};

/**
 * Accounts that appear in Selected Accounts (Payee dropdown selection).
 * Includes payee chips + explicit "Payer:" chips — not the top Payer-scope dropdown.
 * Used for GET-mapped form state → POST/PUT `accountNumbers` (API expectation).
 */
export const selectedCustomerAccountIds = (form) =>
  distinctAccountIds([
    ...(Array.isArray(form.payeeAccountNumbers) ? form.payeeAccountNumbers : []),
    ...(Array.isArray(form.payeeUiPayerNumbers) ? form.payeeUiPayerNumbers : []),
  ]);

const selectedAccountIds = selectedCustomerAccountIds;

const annualRows = (years) => {
  if (!Array.isArray(years) || !years.length) return [];
  return years
    .filter(
      (year) =>
        year.label?.trim() ||
        year.startDate ||
        year.endDate ||
        year.commitment?.trim() ||
        year.discount?.trim()
    )
    .map((year) => ({
      year_label: String(year.label || "").trim(),
      start_date: year.startDate || undefined,
      end_date: year.endDate || undefined,
      commitment_amount: toMoney(year.commitment),
      discount_percent: toPercent(year.discount),
    }));
};

const tierRows = (tiers) => {
  if (!Array.isArray(tiers) || !tiers.length) return [];
  return tiers
    .filter((tier) => tier.name?.trim() || tier.threshold?.trim() || tier.discount?.trim())
    .map((tier) => ({
      tier_name: String(tier.name || "").trim(),
      minimum_spend_threshold: toMoney(tier.threshold),
      retail_discount_percent: toPercent(tier.discount),
    }));
};

const serviceExclusionCodes = (form) => {
  if (!Array.isArray(form.serviceExclusions) || !form.serviceExclusions.length) return undefined;
  const codes = form.serviceExclusions
    .map((label) => SERVICE_EXCLUSION[label] || String(label || "").trim())
    .filter(Boolean);
  return codes.length ? [...new Set(codes)] : undefined;
};

const wholesaleProgramRefs = (form) => {
  if (!Array.isArray(form.wholesalePrograms) || !form.wholesalePrograms.length) return undefined;
  const refs = form.wholesalePrograms
    .filter((ref) => ref?.program_type && ref?.program_id)
    .map((ref) => ({
      program_type: ref.program_type,
      program_id: ref.program_id,
    }));
  return refs.length ? refs : undefined;
};

/** Exclusive commitment structure: annual year rows XOR volume discount tiers. */
export const resolveCommitmentMode = (formOrYears, tiersArg) => {
  if (formOrYears && typeof formOrYears === "object" && !Array.isArray(formOrYears)) {
    if (formOrYears.commitmentMode === "tiers" || formOrYears.commitmentMode === "annual") {
      return formOrYears.commitmentMode;
    }
    const years = formOrYears.years;
    const tiers = formOrYears.tiers;
    if (Array.isArray(tiers) && tiers.length && !(Array.isArray(years) && years.length)) {
      return "tiers";
    }
    return "annual";
  }
  const years = formOrYears;
  const tiers = tiersArg;
  if (Array.isArray(tiers) && tiers.length && !(Array.isArray(years) && years.length)) {
    return "tiers";
  }
  return "annual";
};

/**
 * Commercial fields shared by Java partnerCentral and Python POST/PUT.
 * Never includes identity, contacts, accounts, customer_orgcode, or audits.
 * Commitment structure is exclusive: annual breakdown XOR volume discount tiers.
 */
export const buildCommercialFields = (form, status) => {
  const body = {
    status: status === "draft" ? "DRAFT" : "ACTIVE",
  };

  setIf(body, "customer_code", String(form.customerId || "").trim());

  if (Array.isArray(form.regions) && form.regions.length) {
    body.primary_operating_regions = [
      ...new Set(form.regions.map((r) => String(r || "").trim()).filter(Boolean)),
    ];
  } else if (status !== "draft") {
    // omit on draft; send [] only when clearing on edit via explicit empty — create omits
  }

  const programs = wholesaleProgramRefs(form);
  if (programs) body.wholesale_programs = programs;

  setIf(body, "contract_start_date", form.startDate || undefined);
  setIf(body, "contract_end_date", form.endDate || undefined);
  setIf(body, "payment_terms_days", paymentTermsDays(form.paymentTerms));
  setIf(body, "multi_year_commitment_amount", toMoney(form.multiYearCommitment));
  setIf(body, "discount_percent", toPercent(form.commitmentDiscount));

  const mode = resolveCommitmentMode(form);
  if (mode === "tiers") {
    body.annual_commitment_breakdown = [];
    body.downstream_volume_discount_tiers = tierRows(form.tiers);
  } else {
    body.annual_commitment_breakdown = annualRows(form.years);
    body.downstream_volume_discount_tiers = [];
  }

  setIf(body, "pricing_model", PRICING_MODEL[form.pricingModel]);

  const exclusions = serviceExclusionCodes(form);
  if (exclusions) body.service_exclusions = exclusions;

  return body;
};

/**
 * Java POST /organization/customer — new customer onboarding + optional partnerCentral.
 */
export const buildJavaOnboardingPayload = (form, status) => {
  const accounts = selectedAccountIds(form);
  const body = {
    orgname: String(form.organizationName || "").trim(),
    orgprimaryname: String(form.primaryContactName || "").trim(),
    orgprimaryemail: String(form.primaryContactEmail || "").trim(),
    accountNumbers: accounts,
    partnerCentral: buildCommercialFields(form, status),
  };
  return body;
};

/**
 * Python PUT /cloudnova/partner-central/customer?id= — commercial terms only.
 * Must not include identity, contacts, or aws_account_numbers.
 */
export const buildCommercialProfilePayload = (form, status) => {
  const body = buildCommercialFields(form, status);

  // Collections: send full replacement when present in form (including clear with []).
  body.primary_operating_regions = Array.isArray(form.regions)
    ? [...new Set(form.regions.map((r) => String(r || "").trim()).filter(Boolean))]
    : [];
  body.wholesale_programs = wholesaleProgramRefs(form) || [];
  body.service_exclusions = serviceExclusionCodes(form) || [];
  // annual_commitment_breakdown / downstream_volume_discount_tiers set exclusively above

  return body;
};

/**
 * Java PUT /organization/customer — additive account sync (+ required active flag).
 */
export const buildJavaAccountUpdatePayload = (form, { active = true } = {}) => {
  const orgcode = String(form.customerOrgcode || "").trim();
  const accounts = selectedAccountIds(form);
  return {
    orgcode,
    active: Boolean(active),
    accountNumbers: accounts,
  };
};

/**
 * Java PUT /organization/customer — org/contact/address/billing + optional partnerCentral.
 * Partial update: empty values are omitted so unchanged server fields remain.
 * Same nested commercial field names as POST (incl. discount_percent).
 */
export const buildJavaCustomerUpdatePayload = (form, { active = true, status = "saved" } = {}) => {
  const body = {
    orgcode: String(form.customerOrgcode || "").trim(),
    active: Boolean(active),
  };

  setIf(body, "orgname", String(form.organizationName || "").trim());
  setIf(body, "orgdescription", String(form.orgDescription || "").trim());
  setIf(body, "orgprimaryname", String(form.primaryContactName || "").trim());
  setIf(body, "orgprimaryemail", String(form.primaryContactEmail || "").trim());
  setIf(body, "phoneNumber", String(form.phoneNumber || "").trim());
  setIf(body, "orgaddress", String(form.orgAddress || "").trim());
  setIf(body, "orgcity", String(form.orgCity || "").trim());
  setIf(body, "orgstate", String(form.orgState || form.orgProvince || "").trim());
  setIf(body, "orgcountry", String(form.orgCountry || "").trim());
  setIf(body, "orgzip", String(form.orgZip || form.orgZipcode || "").trim());

  const billingName = String(form.orgBillingName || form.primaryContactName || "").trim();
  const billingEmail = String(form.orgBillingEmail || form.primaryContactEmail || "").trim();
  const billingAddress = String(form.orgBillingAddress || form.orgAddress || "").trim();
  const billingCity = String(form.orgBillingCity || form.orgCity || "").trim();
  const billingState = String(
    form.orgBillingState || form.orgState || form.orgProvince || ""
  ).trim();
  const billingCountry = String(form.orgBillingCountry || form.orgCountry || "").trim();
  const billingZip = String(form.orgBillingZip || form.orgZip || form.orgZipcode || "").trim();

  setIf(body, "orgbillingname", billingName);
  setIf(body, "orgbillingemail", billingEmail);
  setIf(body, "orgbillingaddress", billingAddress);
  setIf(body, "orgbillingcity", billingCity);
  setIf(body, "orgbillingstate", billingState);
  setIf(body, "orgbillingcountry", billingCountry);
  setIf(body, "orgbillingzip", billingZip);

  const accounts = selectedAccountIds(form);
  if (accounts.length) body.accountNumbers = accounts;

  body.partnerCentral = buildCommercialFields(form, status);
  return body;
};

/** @deprecated Use buildJavaOnboardingPayload / buildCommercialProfilePayload. */
export const buildCustomerCreatePayload = (form, status) =>
  buildCommercialProfilePayload(form, status);

const invertMap = (map) =>
  Object.fromEntries(Object.entries(map).map(([label, code]) => [code, label]));

const LABEL_PRICING_MODEL = invertMap(PRICING_MODEL);
const LABEL_SERVICE_EXCLUSION = invertMap(SERVICE_EXCLUSION);

const asText = (value) => (value == null ? "" : String(value));

const formatMoneyField = (value) => {
  if (value == null || value === "") return "";
  const amount = parseNumber(value);
  if (amount == null || Number.isNaN(amount)) return asText(value);
  return amount.toLocaleString("en-US", { maximumFractionDigits: 2 });
};

const formatPercentField = (value) => {
  if (value == null || value === "") return "";
  const amount = parseNumber(value);
  if (amount == null || Number.isNaN(amount)) return asText(value);
  return String(amount);
};

const paymentTermsLabel = (days) => {
  if (days == null || days === "") return "";
  const n = Number(days);
  return Number.isFinite(n) ? `Net ${n}` : "";
};

/**
 * Maps a Partner Central Customer GET/list record into Add Customer form state.
 */
export const formFromCustomerRecord = (record) => {
  const payeeAccountNumbers = distinctAccountIds(record.aws_account_numbers);
  const years = Array.isArray(record.annual_commitment_breakdown)
    ? record.annual_commitment_breakdown.map((row, index) => ({
        id: `year-loaded-${index}`,
        label: asText(row.year_label),
        startDate: row.start_date || "",
        endDate: row.end_date || "",
        commitment: formatMoneyField(row.commitment_amount),
        discount: formatPercentField(row.discount_percent),
      }))
    : [];
  const tiers = Array.isArray(record.downstream_volume_discount_tiers)
    ? record.downstream_volume_discount_tiers.map((row, index) => ({
        id: `tier-loaded-${index}`,
        name: asText(row.tier_name),
        threshold: formatMoneyField(row.minimum_spend_threshold),
        discount: formatPercentField(row.retail_discount_percent),
      }))
    : [];
  const wholesalePrograms = Array.isArray(record.wholesale_programs)
    ? record.wholesale_programs
        .filter((ref) => ref?.program_type && ref?.program_id)
        .map((ref) => ({
          program_type: ref.program_type,
          program_id: ref.program_id,
        }))
    : [];
  const serviceExclusions = Array.isArray(record.service_exclusions)
    ? record.service_exclusions
        .map((code) => LABEL_SERVICE_EXCLUSION[code] || code)
        .filter(Boolean)
    : [];

  return {
    organizationName: asText(record.organization_name),
    customerId: asText(record.customer_code),
    primaryContactName: asText(record.primary_contact_name),
    primaryContactEmail: asText(record.primary_contact_email),
    customerOrgcode: asText(record.customer_orgcode),
    organizationId: record.organization_id != null ? record.organization_id : null,
    profileId: record.id != null ? record.id : null,
    initialAccountNumbers: payeeAccountNumbers,
    payerAccountNumbers: [],
    payeeAccountNumbers,
    payeeUiPayerNumbers: [],
    regions: Array.isArray(record.primary_operating_regions)
      ? [...record.primary_operating_regions]
      : [],
    wholesalePrograms,
    startDate: record.contract_start_date || "",
    endDate: record.contract_end_date || "",
    paymentTerms: paymentTermsLabel(record.payment_terms_days),
    multiYearCommitment: formatMoneyField(record.multi_year_commitment_amount),
    commitmentDiscount: formatPercentField(record.discount_percent),
    pricingModel: LABEL_PRICING_MODEL[record.pricing_model] || "",
    serviceExclusions,
    commitmentMode: resolveCommitmentMode(years, tiers),
    years,
    tiers,
  };
};

/**
 * Flatten Java GET /organization/customer into the commercial-profile shape
 * expected by formFromCustomerRecord, then attach org identity fields.
 */
export const formFromOrganizationCustomer = (record) => {
  if (!record || typeof record !== "object") return formFromCustomerRecord({});

  const pc =
    record.partnerCentral && typeof record.partnerCentral === "object"
      ? record.partnerCentral
      : {};
  const accounts = Array.isArray(record.accountNumbers)
    ? record.accountNumbers
    : Array.isArray(record.aws_account_numbers)
      ? record.aws_account_numbers
      : [];

  const flat = {
    id: record.partner_central_customer_id ?? pc.id ?? record.id,
    organization_id: record.organization_id,
    customer_orgcode: record.orgcode || record.customer_orgcode,
    organization_name: record.orgname || record.organization_name,
    primary_contact_name: record.orgprimaryname || record.primary_contact_name,
    primary_contact_email: record.orgprimaryemail || record.primary_contact_email,
    aws_account_numbers: accounts,
    customer_code: pc.customer_code,
    primary_operating_regions: pc.primary_operating_regions,
    contract_start_date: pc.contract_start_date,
    contract_end_date: pc.contract_end_date,
    payment_terms_days: pc.payment_terms_days,
    multi_year_commitment_amount: pc.multi_year_commitment_amount,
    discount_percent: pc.discount_percent,
    annual_commitment_breakdown: pc.annual_commitment_breakdown,
    downstream_volume_discount_tiers: pc.downstream_volume_discount_tiers,
    pricing_model: pc.pricing_model,
    service_exclusions: pc.service_exclusions,
    wholesale_programs: pc.wholesale_programs,
    status: pc.status,
  };

  const commercial = formFromCustomerRecord(flat);

  return {
    ...commercial,
    organizationName: asText(record.orgname) || commercial.organizationName,
    primaryContactName:
      asText(record.orgprimaryname) || commercial.primaryContactName,
    primaryContactEmail:
      asText(record.orgprimaryemail) || commercial.primaryContactEmail,
    customerOrgcode: asText(record.orgcode) || commercial.customerOrgcode,
    organizationId:
      record.organization_id != null
        ? record.organization_id
        : commercial.organizationId,
    profileId:
      record.partner_central_customer_id != null
        ? record.partner_central_customer_id
        : commercial.profileId,
    orgDescription: asText(record.orgdescription),
    phoneNumber: asText(record.phoneNumber),
    orgAddress: asText(record.orgaddress),
    orgCity: asText(record.orgcity),
    orgProvince: asText(record.orgstate),
    orgState: asText(record.orgstate),
    orgCountry: asText(record.orgcountry),
    orgZipcode: asText(record.orgzip),
    orgZip: asText(record.orgzip),
    orgBillingName: asText(record.orgbillingname),
    orgBillingEmail: asText(record.orgbillingemail),
    orgBillingAddress: asText(record.orgbillingaddress),
    orgBillingCity: asText(record.orgbillingcity),
    orgBillingState: asText(record.orgbillingstate),
    orgBillingCountry: asText(record.orgbillingcountry),
    orgBillingZip: asText(record.orgbillingzip),
    active: record.active,
  };
};

/** Client-side ACTIVE completeness aligned with the Customer module contract. */
export const validateActiveCustomer = (form) => {
  const next = {};
  if (!String(form.organizationName || "").trim()) next.organizationName = "Enter an organization name.";
  if (!String(form.primaryContactName || "").trim()) next.primaryContactName = "Enter a primary contact name.";
  if (!String(form.primaryContactEmail || "").trim()) {
    next.primaryContactEmail = "Enter a primary contact email.";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.primaryContactEmail.trim())) {
    next.primaryContactEmail = "Enter a valid email address.";
  }
  if (!selectedAccountIds(form).length) {
    next.payerAccountNumbers = "Select at least one AWS account for this customer.";
  }
  if (form.startDate && form.endDate && form.endDate < form.startDate) {
    next.endDate = "End date must be on or after the start date.";
  }
  return next;
};
